import React, { FC, useEffect } from 'react';
import Breadcrumbs from '@mos-front-components/public/es/components/Breadcrumbs';

import Button from '@components/Button/index';
import MapWrapper from '@components/MapWrapper';
import { TRACK_SIGN_UP_MAIN_PAGE_CLICK } from '@services/stats';
import { Box } from '../../ds/Box/Box';
import { Heading } from '../../ds/Heading/Heading';
import { Stack } from '../../ds/Stack/Stack';
import { Paragraph } from '../../ds/Paragraph/Paragraph';
import { Message } from '../../ds/Message/Message';
import AboutServiceBtn from './private/AboutServiceBtn';
import { PrintList } from './private/PrintList';

import st from './MainHeader.module.css';

const plural = require('plural-ru');

type MainHeaderProps = {
  phone: boolean;
  showMap: () => void;
  numberOfPoints: number;
  onServicesCatalogOpen: () => void;
  presentationalOfficesCount: number;
};

const MainHeader: FC<MainHeaderProps> = ({
  phone,
  showMap,
  numberOfPoints,
  onServicesCatalogOpen,
  presentationalOfficesCount,
}) => {
  const mobileBreadcrumbs = [
    {
      url: `${window.location.origin}/services/`,
      name: 'Услуги',
    },
  ];

  const officesCount = presentationalOfficesCount ?? numberOfPoints;

  const desktopBreadcrumbs = [
    ...mobileBreadcrumbs,
    {
      url: `${window.location.origin}/services/centry-gosudarstvennyh-uslug/`,
      name: 'Центры государственных услуг г. Москвы «Мои документы»',
    },
  ];

  useEffect(() => {
    global.mosTizer?.renderAll?.();
  }, []);

  return (
    <div className={st.header}>
      <div className={st.top}>
        <Breadcrumbs
          breadcrumbs={phone ? mobileBreadcrumbs : desktopBreadcrumbs}
          className={st.breadcrumbs}
        />
        <AboutServiceBtn />
      </div>
      <div className={st.messageWrap}>
            <h1 className={st.title}>
              Мои документы
              <div className={st.subTitle}>
                Центры государственных услуг г. Москвы
              </div>
            </h1>
            <Message kind="info" className={st.message}>
              <Stack space="sp-8">
                <Box>
                  <Heading level="4" as="span">
                    По вопросам миграции записывайтесь через портал госуслуги
                  </Heading>
                </Box>
                <Box>
                  <Paragraph className={st.messageText}>
                    Запись в&nbsp;подразделения по&nbsp;вопросам миграции ГУ МВД России по&nbsp;Москве, размещенных
                    в&nbsp;центрах госуслуг «Мои&nbsp;документы», осуществляется через&nbsp;портал госуслуг РФ. Изменения действительны с&nbsp;20.12.2024&nbsp;г.
                  </Paragraph>
                </Box>
              </Stack>
            </Message>
      </div>
      <div className={st.content}>
        <div className={st.note}>
          <div className={st.noteInnerWrap}>
            <div className={st.description}>
              <p>
                Центры госуслуг — это более 130 офисов «Мои&nbsp;документы» в&nbsp;Москве,
                включая флагманские офисы и&nbsp;Дворец госуслуг на&nbsp;ВДНХ, где
                ежедневно быстро и&nbsp;с&nbsp;комфортом можно получить более 300
                государственных услуг, 99% из&nbsp;которых предоставляются без&nbsp;привязки
                к&nbsp;месту регистрации. В&nbsp;центрах госуслуг есть
                возможность оформить ряд услуг «одним&nbsp;пакетом» в&nbsp;рамках 9
                жизненных ситуаций или&nbsp;подать комплексный запрос. «Мои&nbsp;документы»
                принимают участие в&nbsp;городских проектах. Подробная
                информация — на&nbsp;портале
                {' '}
                <a
                  className={st.link}
                  data-testid="docsLink"
                  href="https://md.mos.ru/"
                >
                  md.mos.ru
                </a>
                .
              </p>
              <p>
                Вы также можете
                {' '}
                <a
                  className={st.link}
                  data-testid="recordLink"
                  href="https://www.mos.ru/pgu/ru/services/link/2145/"
                >
                  записаться в центры госуслуг и ведомства
                </a>
                {' '}
                Москвы
              </p>
            </div>
{phone && (<div className={st.info}>
        <Box>
                  <Heading level="3" className={st.heading}>
                    Распечатайте бумажный документ в&nbsp;центре «Мои&nbsp;документы»
                  </Heading>
                  <Paragraph className={st.messageText}>
                    Если вы оформили услугу через портал госуслуг РФ, и&nbsp;вам необходим бумажный экземпляр, вы можете бесплатно распечатать результат услуги в&nbsp;любом центре «Мои&nbsp;документы»
                  </Paragraph>
                  <Heading level="4" className={st.heading4}>
                    Можно распечатать в центре госуслуг:
                  </Heading>
                  <ul className={st.messageText}>
                    <PrintList/>
                  </ul>
                </Box>
      </div>)}
            {phone && (
              <Button
                dataTestId="openModalButton"
                onClick={() => {
                  TRACK_SIGN_UP_MAIN_PAGE_CLICK();
                  onServicesCatalogOpen();
                }}
              >
                Выбрать услугу
              </Button>
            )}
          </div>
        </div>
        <div
          className={st.tizerWrap}
          data-mos-teaser='{"placementParams":{"p1":"mfc"}}'
        />
        {phone && (
          <div className={st.map}>
            <MapWrapper
              withRating={false}
              mapOptions={{
                zoomControl: false,
                fullscreenControl: false,
                locationControl: false,
              }}
            />
            <div className={st.mapOverlay}>
              {officesCount > 0 && (
                <div className={st.mapOverlayText}>
                  {officesCount}
                  {' '}
                  {plural(officesCount, ' офис', ' офиса', ' офисов')}
                  <br />
                  госуслуг
                </div>
              )}
              <button
                data-testid="showMapButton"
                type="button"
                className={st.showOnMapButton}
                onClick={showMap}
              >
                Показать на карте
              </button>
            </div>
          </div>
        )}
      </div>
      {!phone && (<div className={st.info}>
        <Box>
                  <Heading level="3" className={st.heading}>
                    Распечатайте бумажный документ в&nbsp;центре «Мои&nbsp;документы»
                  </Heading>
                  <Paragraph className={st.messageText}>
                    Если вы оформили услугу через портал госуслуг РФ, и&nbsp;вам необходим бумажный экземпляр, вы можете бесплатно распечатать результат услуги в&nbsp;любом центре «Мои&nbsp;документы»
                  </Paragraph>
                  <Heading level="4" className={st.heading4}>
                    Можно распечатать в центре госуслуг:
                  </Heading>
                  <ul className={st.messageText}>
                    <PrintList/>
                  </ul>
                </Box>
      </div>)}
    </div>
  );
};

export default MainHeader;
